import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Pencil, ListChecks, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { API, formatINR } from "@/lib/api";
import { useDash } from "@/pages/dashboard/DashboardLayout";
import BookDateModal from "@/pages/dashboard/BookDateModal";
import { Skeleton } from "@/components/ui/skeleton";

export default function BookingsView() {
  const { businessId, venues, user, businessName, businessHasLogo } = useDash();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const venueMap = Object.fromEntries(venues.map((v) => [v.venue_id, v]));

  const { data: bookings = [], isLoading } = useQuery({
    queryKey: ["all-bookings", businessId],
    queryFn: async () => (await api.get("/bookings", { params: { business_id: businessId } })).data,
    enabled: !!businessId,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["all-bookings"] });
    qc.invalidateQueries({ queryKey: ["bookings"] });
    qc.invalidateQueries({ queryKey: ["revenue"] });
    qc.invalidateQueries({ queryKey: ["venues"] });
  };

  const exportCsv = async () => {
    try {
      const res = await fetch(`${API}/bookings/export?business_id=${businessId || ""}`, { credentials: "include" });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "pulse_bookings.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV exported");
    } catch { toast.error("Export failed"); }
  };

  const sorted = [...bookings].sort((a, b) => b.date.localeCompare(a.date));

  const exportPdf = () => {
    const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const rows = sorted.map((b) => {
      const v = venueMap[b.venue_id];
      return `<tr><td>${new Date(b.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</td><td>${esc(v?.name || "—")}</td><td>${esc(b.booker_name)}</td><td>${esc(b.client_name || "—")}</td><td style="text-align:right">₹${Number(b.amount || 0).toLocaleString("en-IN")}</td><td style="text-transform:capitalize">${esc(b.status)}</td></tr>`;
    }).join("");
    const total = sorted.reduce((a, b) => a + (b.amount || 0), 0);
    const logo = businessHasLogo ? `<img src="${API}/business/${businessId}/logo" style="height:56px;width:56px;border-radius:50%;object-fit:cover;border:1px solid #e2e8f0" />` : "";
    const html = `<!doctype html><html><head><meta charset="utf-8" /><title>${esc(businessName || "Pulse")} — Bookings</title><style>
      body{font-family:Arial,Helvetica,sans-serif;color:#0A192F;padding:32px;margin:0}
      .hdr{display:flex;align-items:center;gap:14px;border-bottom:2px solid #0A192F;padding-bottom:16px}
      .hdr h1{margin:0;font-size:20px} .hdr .sub{color:#64748B;font-size:12px;margin-top:3px}
      table{width:100%;border-collapse:collapse;margin-top:22px;font-size:12px}
      th{text-align:left;background:#f1f5f9;padding:9px;text-transform:uppercase;font-size:10px;letter-spacing:.04em;color:#64748B}
      td{padding:9px;border-bottom:1px solid #eef2f7}
      .tot{margin-top:16px;text-align:right;font-size:15px;font-weight:bold}
      .foot{margin-top:30px;color:#94a3b8;font-size:10px}
      @media print{body{padding:0}}
    </style></head><body>
      <div class="hdr">${logo}<div><h1>${esc(businessName || "Pulse")}</h1><div class="sub">Bookings sheet · generated ${new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</div></div></div>
      <table><thead><tr><th>Date</th><th>Venue</th><th>Booker</th><th>Client</th><th style="text-align:right">Amount</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="tot">Total revenue: ₹${total.toLocaleString("en-IN")}</div>
      <div class="foot">Powered by Pulse — A product by Jbros Innovation Pvt. Ltd.</div>
    </body></html>`;
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow pop-ups to export the sheet"); return; }
    w.document.write(html); w.document.close();
    const go = () => { try { w.focus(); w.print(); } catch (e) {} };
    const img = w.document.querySelector("img");
    if (img && !img.complete) { img.onload = go; img.onerror = go; setTimeout(go, 1800); }
    else setTimeout(go, 350);
    toast.success("Opening branded sheet…");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-pulse-navy">Bookings</h1>
          <p className="text-sm text-slate">Every booking across your venues.</p>
        </div>
        <div className="flex flex-wrap gap-2 self-start">
          <button onClick={exportPdf} data-testid="export-pdf-btn"
            className="flex items-center gap-2 rounded-md bg-pulse-navy px-5 py-2.5 text-sm font-semibold text-white transition-transform active:scale-[0.98]">
            <FileText size={16} /> Export PDF
          </button>
          <button onClick={exportCsv} data-testid="export-csv-btn"
            className="flex items-center gap-2 rounded-md border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-pulse-navy transition-colors hover:bg-cloud">
            <Download size={16} /> Export CSV
          </button>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-pulse-sm">
        {isLoading ? (
          <div className="space-y-3 p-5">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
        ) : sorted.length === 0 ? (
          <div className="py-16 text-center">
            <ListChecks className="mx-auto text-slate/50" size={36} />
            <p className="mt-3 font-medium text-slate">No bookings yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bookings-table">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs font-medium uppercase tracking-wide text-slate">
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3">Venue</th>
                  <th className="px-5 py-3">Booker</th>
                  <th className="px-5 py-3 hidden sm:table-cell">Client</th>
                  <th className="px-5 py-3">Amount</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {sorted.map((b) => {
                  const v = venueMap[b.venue_id];
                  return (
                    <tr key={b.booking_id} className="transition-colors hover:bg-cloud/50" data-testid={`booking-row-${b.booking_id}`}>
                      <td className="whitespace-nowrap px-5 py-3 font-medium text-charcoal">{new Date(b.date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1.5 rounded px-2 py-0.5 text-xs font-medium" style={{ background: `${v?.color}18`, color: v?.color }}>
                          <span className="h-1.5 w-1.5 rounded-full" style={{ background: v?.color }} />{v?.name || "—"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-charcoal">{b.booker_name}</td>
                      <td className="px-5 py-3 hidden text-slate sm:table-cell">{b.client_name || "—"}</td>
                      <td className="px-5 py-3 font-semibold text-pulse-navy">{formatINR(b.amount)}</td>
                      <td className="px-5 py-3">
                        <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${b.status === "confirmed" ? "bg-pulse-green/10 text-pulse-green" : "bg-amber-100 text-amber-700"}`}>{b.status}</span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => { setEditing(b); setModalOpen(true); }} data-testid={`edit-booking-${b.booking_id}`}
                          className="grid h-8 w-8 place-items-center rounded-md text-slate transition-colors hover:bg-cloud hover:text-pulse-navy"><Pencil size={15} /></button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <BookDateModal open={modalOpen} onClose={() => setModalOpen(false)} venues={venues} businessId={businessId}
        editing={editing} currentUserName={user?.name} onSaved={refresh} />
    </div>
  );
}
